import "./Comments.scss";
import Comment from "./Comment";
import useUser from "../../contexts/userContext/useUser";
import {useEffect, useState} from "react";
import CommentModal from "./CommentModal";
import Button from "../Button/Button";
import Icon, {plusIcon} from "../Icon/Icon";
import {CellSelection} from "@tiptap/pm/tables";
import CommentActions from "../../api/CommentActions";
import DeleteModal from "../DeleteModal/DeleteModal";
import {useNotification} from "../../hooks/useNotification";
import {TextSelection} from "prosemirror-state";


const COMMENT_HEIGHT = 100;
const COMMENT_GAP = 5;

const Comments = ({commentButtonPos, lastSelection, checkpointId, editor, documentId, nextCheckpointId}) => {
    const {user} = useUser();
    const [positions, setPositions] = useState({});
    const [comments, setComments] = useState([]);
    const [showModal, setShowModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);
    const {triggerNotification} = useNotification();

    const handleSaveComment = (comment) => {
        const {from} = lastSelection;
        const node = editor.state.doc.nodeAt(from);
        const selectionType = editor.state.selection;

        if (node?.type.name === "variableTable") {
            editor.chain().focus()
                .setNodeSelection(from)
                .updateAttributes("variableTable", {
                    commentId: comment.id,
                    commentState: "current",
                })
                .run();
        } else if (selectionType instanceof CellSelection) {
            const {state, view} = editor;

            let tr = state.tr;

            selectionType.forEachCell((cellNode, cellPos) => {
                const $pos = state.doc.resolve(cellPos + 1);

                const from = $pos.start();
                const to = $pos.end();

                tr.addMark(from, to, state.schema.marks.comment.create({commentId: comment.id,}));
            })

            view.dispatch(tr);
        } else {
            editor.chain().focus()
                .setTextSelection(lastSelection)
                .setMark("comment", {commentId: comment.id, commentState: "current"})
                .run()
        }

        setComments((prev) => [...prev, comment]);
        setShowModal(false);
    }

    const handleCommentUpdate = (response) => {
        setComments(prev => prev.map(comment => comment.id === response.id ? response : comment));
        setShowModal(false);
    }

    const handleCommentDelete = () => {
        const {id} = deleteModal;

        CommentActions.remove(id, () => {
            setComments(prev => prev.filter(comment => comment.id !== id));
            setDeleteModal(null);
            triggerNotification("Comment deleted successfully");
        })
    }

    const handleCommentResolve = (response) => {
        setComments(prev => prev.map(comment => comment.id === response.id ? response : comment));
    }


    const resolvePositions = () => {
        const wrapper = document.querySelector(".editor-wrapper");
        if (!wrapper) return;

        const wrapperRect = wrapper.getBoundingClientRect();

        const commentList = comments.map(comment => {
            const el = document.querySelector(`span[commentid="${comment.id}"]`) ?? document.querySelector(`[commentid="${comment.id}"]`)
            if (!el) return null;

            const rect = el.getBoundingClientRect();
            return {id: comment.id, y: rect.top - wrapperRect.top + wrapper.scrollTop, height: COMMENT_HEIGHT};
        })
            .filter(Boolean)
            .sort((a, b) => a.y - b.y);

        if (commentButtonPos) {
            commentList.push({id: "modal", y: commentButtonPos.y, height: 125});
            commentList.sort((a, b) => a.y - b.y);
        }

        for (let i = 1; i < commentList.length; i++) {
            const previous = commentList[i - 1];
            const current = commentList[i];

            if (current.id === "modal") continue;

            const minY = previous.y + previous.height + COMMENT_GAP;
            if (current.y < minY) current.y = minY;
        }

        for (let i = commentList.length - 2; i >= 0; i--) {
            const current = commentList[i];
            const next = commentList[i + 1];

            if (current.id === "modal") continue;

            const maxY = next.y - current.height - COMMENT_GAP;
            if (current.y > maxY) current.y = maxY;
        }

        const map = {};

        commentList.forEach(item => {
            map[item.id] = item.y;
        });

        setPositions(map);
    };

    useEffect(() => {
        if (!documentId || !checkpointId) return;

        CommentActions.getAll(documentId, nextCheckpointId ? nextCheckpointId : checkpointId, setComments)
    }, [documentId, checkpointId, nextCheckpointId])

    useEffect(() => {
        const {tr, doc} = editor.state;

        doc.descendants((node, pos) => {
            if (node.type.name === "variable" || node.type.name === "variableList") {
                const nodeStart = pos;
                const nodeEnd = pos + node.nodeSize;
                const commentMark = editor.state.schema.marks.comment;
                const hasMark = editor.state.doc.rangeHasMark(nodeStart, nodeEnd, commentMark);

                if (!hasMark) return;
                let foundMark = null;

                node.marks?.forEach(mark => {
                    if (mark.type.name === "comment") foundMark = mark;
                });

                if (!foundMark) return;
                const comment = comments.find(c => c.id === foundMark.attrs.commentId);

                if (!comment) {
                    tr.removeMark(nodeStart, nodeEnd, foundMark);
                    return;
                }

                const newState =
                    comment?.resolved &&
                    checkpointId !== comment?.resolvedInCheckpoint?.id
                        ? "resolved"
                        : "current";

                if (foundMark.attrs.commentState === newState) return;

                tr.removeMark(nodeStart, nodeEnd, foundMark);
                tr.addMark(nodeStart, nodeEnd, commentMark.create({...foundMark.attrs, commentState: newState}));
            } else if (node.type.name === "variableTable" && node.attrs.commentId) {
                const comment = comments.find(c => c.id === node.attrs.commentId);

                if (!comment) {
                    tr.setNodeMarkup(pos, undefined, {...node.attrs, commentId: null, commentState: null});
                    return;
                }

                const newState = comment?.resolved && checkpointId !== comment?.resolvedInCheckpoint?.id ? "resolved" : "current";
                if (node.attrs.commentState === newState) return;

                tr.setNodeMarkup(pos, undefined, {...node.attrs, commentState: newState});
            } else if (node.isText) {
                node.marks.forEach(mark => {
                    if (mark.type.name !== "comment") return;
                    const comment = comments.find(c => c.id === mark.attrs.commentId);

                    if (!comment) {
                        tr.removeMark(pos, pos + node.nodeSize, mark);
                        return;
                    }

                    const newState = comment?.resolved && checkpointId !== comment?.resolvedInCheckpoint?.id ? "resolved" : "current";

                    if (mark.attrs.commentState === newState) return;

                    tr.removeMark(pos, pos + node.nodeSize, mark);
                    tr.addMark(pos, pos + node.nodeSize, mark.type.create({...mark.attrs, commentState: newState}));
                })
            }
        })

        tr.setMeta("skipCommentFilter", true);

        const {from} = editor.state.selection;
        tr.setSelection(TextSelection.create(tr.doc, from));

        if (tr.docChanged) {
            editor.view.dispatch(tr);
        }
    }, [comments])

    useEffect(() => {
        resolvePositions();

        editor.on("update", resolvePositions);
        return () => editor.off("update", resolvePositions);
    }, [comments, commentButtonPos])

    return <div className="comments">
        {
            comments.map(comment =>
                <Comment
                    key={comment.id}
                    comment={comment}
                    onResolve={handleCommentResolve}
                    onEdit={(comment) => setShowModal({comment, y: positions[comment.id]})}
                    onDelete={setDeleteModal}
                    isEditing={showModal?.comment?.id === comment.id}
                    posY={positions[comment.id]}
                    checkpointId={checkpointId}
                    editor={editor}
                />
            )
        }

        {showModal &&
            <CommentModal
                user={user}
                comment={showModal.comment}
                checkpointId={nextCheckpointId ?? checkpointId}
                onClose={() => setShowModal(false)}
                onSave={showModal.comment ? handleCommentUpdate : handleSaveComment}
                posY={showModal.y}
                commentButtonPos={commentButtonPos}
            />
        }

        {deleteModal &&
            <DeleteModal
                title="Deleting comment"
                message="Are you sure you want to delete this comment?"
                onClose={() => setDeleteModal(null)}
                onConfirm={handleCommentDelete}
            />
        }

        {commentButtonPos && (
            <Button
                className="add-comment"
                style={{top: commentButtonPos.y, left: commentButtonPos.x - 20}}
                onClick={() => setShowModal({y: positions['modal'] ?? commentButtonPos.y})}
            >
                <Icon svg={plusIcon}/>
                Add
            </Button>
        )}
    </div>
}

export default Comments